import React from "react";

/** Data point shape for the chart */
export interface ChartDataPoint {
  /** Value to be displayed */
  value: number;
  /** Name of the data point */
  name: string;
  /** Any additional properties */
  [key: string]: any;
}

interface ChartMargins {
  /** Top margin */
  top: number;
  /** Right margin */
  right: number;
  /** Bottom margin */
  bottom: number;
  /** Left margin */
  left: number;
}

interface ChartDimensions {
  /** Width in pixels */
  width: number | null;
  /** Height in pixels */
  height: number | null;
}

export interface ChartState {
  /** Chart data points */
  data: ChartDataPoint[];
  /** Description text */
  chartDescription: string;
  /** Label for Y axis */
  yAxisLabel: string;
  /** Custom tooltip renderer */
  tooltipContent?: null | ((args: { data: ChartDataPoint }) => JSX.Element);
  /** Chart margins */
  margin: ChartMargins;
  /** Chart dimensions */
  dimensions: ChartDimensions;
}

export interface ChartContext extends ChartState {
  /** Reference to SVG element */
  svgRef: React.RefObject<SVGSVGElement>;
}

interface ChartContainerProps {
  /** Child elements */
  children: React.ReactNode;
  /** Chart state */
  state: ChartState;
}

import styles from "./StandardBarChart.scss";

const StateContext = React.createContext<ChartContext>({
  data: [],
  chartDescription: "",
  yAxisLabel: "",
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  dimensions: { width: 0, height: 0 },
  svgRef: { current: null },
});

export function ChartContainer({ children, state }: ChartContainerProps) {
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  return (
    <StateContext.Provider value={{ ...state, svgRef }}>
      <svg
        height="100%"
        width="100%"
        ref={svgRef}
        className={styles["chart"]}
        data-testid="chart-container"
      >
        {children}
      </svg>
    </StateContext.Provider>
  );
}

export function useChartContext() {
  const context = React.useContext(StateContext);

  // istanbul ignore next - trivial noop and typeguard
  if (context === undefined) {
    throw new Error(
      "useChartContext must be used within a StatusBarChart's StateProvider"
    );
  }
  return context;
}
